'use client';

import useUserProfile from '@/hooks/useUserProfile';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import produce from 'immer';
import { cloneDeep, first, isEmpty, last, size } from 'lodash-es';
import { fetchViaProxy } from './proxy-fetch';
import { API_BASE_URL } from '@/utils/constants';

/**************** PRIVATE FUNCTIONS AND FETECHERS ******************** */

const createPlaceholderReview = (
  nonProfitId,
  parentReviewId,
  message,
  attachment,
  userProfile,
  selectedProfile,
  rating,
) => {
  return {
    id: Date.now().toString(),
    nonProfitId: nonProfitId,
    message: message,
    postAs: selectedProfile,
    rating: rating,
    parentReviewId: parentReviewId,
    attachmentUrl: attachment?.content,
    createdOn: new Date().toISOString(),
    replies: [],
    totalReplies: 0,
    reactions: {},
    totalReactions: 0,
    userReaction: {
      hasReaction: false,
      reactionType: null,
      as: null,
    },
    author: {
      id: userProfile.id,
      first_name: userProfile.firstName,
      last_name: userProfile.lastName,
      avatar: userProfile.avatar,
      orgDetails: userProfile.activeCommunity,
    },
  };
};

const createReviewAttachment = async (nonProfitId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(
    `/proxy/v2/protected/nonprofits/${nonProfitId}/reviews/attachment`,
    {
      method: 'POST',
      body: formData,
    },
    true,
  );

  if (res.ok) {
    const data = await res.json();
    return data ? data.url : null;
  }
  return null;
};

const updateReviewCache = (review, queryClient, isNew) => {
  const isReply = !!review.parentReviewId;
  const queryKey = getQueryKey(review.nonProfitId, review.parentReviewId);
  const { nonProfitId, id: reviewId, parentReviewId } = review;
  queryClient.setQueryData(queryKey, (oldData) => {
    if (oldData) {
      return produce(oldData, (draft) => {
        if (isNew) {
          const firstPage = first(draft.pages);
          if (isReply) {
            firstPage.reviews = [...firstPage.reviews, review];
          } else {
            firstPage.reviews = [review, ...firstPage.reviews];
          }
        } else {
          const { pageIndex, reviewIndex, review } = getReviewFromCache(
            nonProfitId,
            reviewId,
            parentReviewId,
            queryClient,
          );

          if (pageIndex >= 0 && reviewIndex >= 0) {
            draft.pages[pageIndex].reviews[reviewIndex] = review;
          } else {
            // this should never happen
            console.warn('Failed to update review cache');
          }
        }
      });
    } else {
      return {
        pages: [
          {
            reviews: [review],
            hasMore: false,
          },
        ],
      };
    }
  });
};

const getQueryKey = (nonProfitId, parentReviewId) => {
  const isReply = !!parentReviewId;
  return isReply
    ? ['community-review-replies', parentReviewId]
    : ['community-reviews', nonProfitId];
};

const getReviewFromCache = (
  nonProfitId,
  reviewId,
  parentReviewId,
  queryClient,
) => {
  const queryKey = getQueryKey(nonProfitId, parentReviewId);
  const cache = queryClient.getQueryData(queryKey);
  if (cache) {
    const pageIndex = cache.pages.findIndex((page) => {
      return page.reviews.some((c) => c.id === reviewId);
    });
    if (pageIndex >= 0) {
      const reviewIndex = cache.pages[pageIndex].reviews.findIndex(
        (c) => c.id === reviewId,
      );
      return {
        review: cache.pages[pageIndex].reviews[reviewIndex],
        reviewIndex,
        pageIndex,
      };
    }
  }
  return {
    review: null,
    pageIndex: -1,
    reviewIndex: -1,
  };
};

const removeReviewFromCache = (review, queryClient) => {
  const { pageIndex } = getReviewFromCache(
    review.nonProfitId,
    review.id,
    review.parentReviewId,
    queryClient,
  );

  if (pageIndex >= 0) {
    const queryKey = getQueryKey(review.nonProfitId, review.parentReviewId);

    queryClient.setQueryData(queryKey, (oldData) => {
      return produce(oldData, (draft) => {
        draft.pages[pageIndex].reviews = draft.pages[pageIndex].reviews.filter(
          (c) => c.id !== review.id,
        );
      });
    });
  } else {
    // this should never happen
    console.warn('Failed to remove review reply from cache');
  }
};

const fetchCommunityReviews = async (nonProfitId, { page, before }) => {
  const params = new URLSearchParams({
    limit: page === 0 ? 2 : 10,
  });
  if (before) {
    params.append('before', before);
  }

  const { ok, data } = await fetchViaProxy(
    `/proxy/v2/protected/nonprofits/${nonProfitId}/reviews?${params.toString()}`,
  );

  if (!ok) {
    throw new Error(`Error fetching reviews for community ${nonProfitId}`);
  }

  return data;
};

const fetchPublicCommunityReviews = async (nonProfitId, { page, before }) => {
  const params = new URLSearchParams({
    limit: page === 0 ? 2 : 10,
  });
  if (before) {
    params.append('before', before);
  }

  const res = await fetch(
    `${
      process.env.NEXT_PUBLIC_API_HOST
    }/v1/public/non-profits/${nonProfitId}/reviews?${params.toString()}`,
  );

  if (res.ok) {
    const data = await res.json();
    return data;
  }

  throw new Error(`Error fetching reviews for community ${nonProfitId}`);
};

const fetchReviewReplies = async (nonProfitId, parentReviewId, before) => {
  const params = new URLSearchParams({
    limit: 5,
  });
  if (before) {
    params.append('before', before);
  }

  const { ok, data } = await fetchViaProxy(
    `/proxy/v2/protected/nonprofits/${nonProfitId}/reviews/${parentReviewId}?${params.toString()}`,
  );

  if (!ok) {
    throw new Error(
      `Error fetching review replies for review ${parentReviewId} on community ${nonProfitId}`,
    );
  }

  return data;
};

/*************** HOOKS AND PUBLIC FUNCTIONS ********************** */

export const usefetchCommunityReviews = (nonProfitId) => {
  return useInfiniteQuery({
    queryKey: ['community-reviews', nonProfitId],
    queryFn: ({ pageParam = { page: 0 } }) =>
      fetchCommunityReviews(nonProfitId, pageParam),
    getNextPageParam: (lastPage, pages) => {
      if (lastPage && !lastPage.hasMore) {
        return undefined;
      }

      const lastReviewDate = last(lastPage.reviews)?.createdOn;
      return {
        page: pages.length,
        before: lastReviewDate,
      };
    },
    staleTime: Infinity,
    cacheTime: Infinity,
  });
};

export const usefetchPublicCommunityReviews = (nonProfitId) => {
  return useInfiniteQuery({
    queryKey: ['community-reviews', nonProfitId],
    queryFn: ({ pageParam = { page: 0 } }) =>
      fetchPublicCommunityReviews(nonProfitId, pageParam),
    getNextPageParam: (lastPage, pages) => {
      if (lastPage && !lastPage.hasMore) {
        return undefined;
      }

      const lastReviewDate = last(lastPage.reviews)?.createdOn;
      return {
        page: pages.length,
        before: lastReviewDate,
      };
    },
    staleTime: Infinity,
    cacheTime: Infinity,
  });
};

export const useFetchCommunityReviewReplies = (nonProfitId, parentReview) => {
  return useInfiniteQuery({
    queryKey: ['community-review-replies', parentReview.id],
    queryFn: async ({ pageParam }) => {
      return fetchReviewReplies(nonProfitId, parentReview.id, pageParam);
    },
    enabled: !!parentReview && size(parentReview.replies) > 0,
    initialData: () => {
      const numReplies = size(parentReview.replies);
      if (numReplies === 0) {
        return undefined;
      }
      return {
        pages: [
          {
            reviews: parentReview.replies,
            hasMore: numReplies < parentReview.totalReplies,
          },
        ],
      };
    },
    staleTime: Infinity,
    cacheTime: Infinity,
    getNextPageParam: (lastPage) => {
      if (!lastPage || !lastPage.hasMore) {
        return undefined;
      }
      return first(lastPage.reviews)?.createdOn;
    },
  });
};

export const useAddOrUpdateCommunityReview = () => {
  const userProfile = useUserProfile();
  const selectedProfile = userProfile?.selectedProfile;
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['add-community-review'],
    mutationFn: async ({
      nonProfitId,
      parentReviewId,
      message,
      attachment,
      reviewId,
      rating,
    }) => {
      const isUpdate = !!reviewId;
      const isReply = !!parentReviewId;
      const reviewData = {
        nonProfitId: nonProfitId,
        message: message,
        postAs: selectedProfile,
      };

      if (!isReply) {
        reviewData.rating = rating;
      }

      // follow up with the actual update
      if (attachment?.file) {
        const fileUrl = await createReviewAttachment(
          nonProfitId,
          attachment.file,
        );
        if (fileUrl) {
          reviewData.attachmentUrl = fileUrl;
        } else {
          throw new Error('Error uploading review attachment');
        }
      } else if (
        attachment?.content &&
        attachment?.content?.startsWith(API_BASE_URL)
      ) {
        reviewData.attachmentUrl = attachment.content.replace(
          `${API_BASE_URL}/v1`,
          '',
        );
      } else {
        reviewData.attachmentUrl = null;
      }

      let path = `/proxy/v2/protected/nonprofits/${nonProfitId}/reviews`;
      if (isUpdate) {
        path = `${path}/${reviewId}`;
      } else if (isReply) {
        path = `${path}/${parentReviewId}`;
      }

      const res = await fetchViaProxy(path, {
        method: isUpdate ? 'PUT' : 'POST',
        body: JSON.stringify(reviewData),
      });

      if (!res.ok) {
        throw new Error(`Error adding review to community ${nonProfitId}`);
      }

      return res.data;
    },
    onMutate: async ({
      nonProfitId,
      parentReviewId,
      message,
      attachment,
      reviewId,
      rating,
    }) => {
      const isUpdate = !!reviewId;

      const fromCache = getReviewFromCache(
        nonProfitId,
        reviewId,
        parentReviewId,
        queryClient,
      );

      let review = createPlaceholderReview(
        nonProfitId,
        parentReviewId,
        message,
        attachment,
        userProfile,
        selectedProfile,
        rating,
      );
      if (isUpdate) {
        review = {
          ...fromCache.review,
          message,
          rating,
          attachmentUrl: attachment?.content,
        };
      }

      // do an optimistic update
      updateReviewCache(review, queryClient, !isUpdate);

      return {
        optimisticData: review,
        cached: fromCache.review,
      };
    },
    onError: (_err, { reviewId }, context) => {
      const { optimisticData, cached } = context;
      const isUpdate = !!reviewId;
      if (isUpdate) {
        updateReviewCache(cached, queryClient, false);
      } else {
        removeReviewFromCache(optimisticData, queryClient);
      }
    },
    onSuccess: (serverData, _var, context) => {
      // apply the server response to the cache
      const { optimisticData } = context;

      // TODO - Shyam - handle reward
      const { review, reward, totalReviews } = serverData;

      // replace the optimistic data with the server data
      const { pageIndex, reviewIndex } = getReviewFromCache(
        optimisticData.nonProfitId,
        optimisticData.id,
        optimisticData.parentReviewId,
        queryClient,
      );

      if (pageIndex >= 0 && reviewIndex >= 0) {
        const queryKey = getQueryKey(review.nonProfitId, review.parentReviewId);
        queryClient.setQueryData(queryKey, (oldData) => {
          return produce(oldData, (draft) => {
            draft.pages[pageIndex].reviews[reviewIndex] = review;
          });
        });
      } else {
        // this should never happen
        console.warn('Failed to update review cache');
      }
    },
  });
};

export const useDeleteCommunityReview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['delete-community-review'],
    mutationFn: async (review) => {
      const { nonProfitId, id: reviewId } = review;
      const { ok, data } = await fetchViaProxy(
        `/proxy/v2/protected/nonprofits/${nonProfitId}/reviews/${reviewId}`,
        {
          method: 'DELETE',
        },
      );

      if (!ok) {
        throw new Error(`Error deleting review ${reviewId}`);
      }
      return data.totalReviews;
    },
    onMutate: async (review) => {
      // update the state
      const { pageIndex, reviewIndex } = getReviewFromCache(
        review.nonProfitId,
        review.id,
        review.parentReviewId,
        queryClient,
      );
      removeReviewFromCache(review, queryClient);
      return { review, pageIndex, reviewIndex };
    },
    onError: (_err, _vars, context) => {
      const { review, pageIndex, reviewIndex } = context;
      const queryKey = getQueryKey(review.nonProfitId, review.parentReviewId);
      queryClient.setQueryData(queryKey, (oldData) => {
        return produce(oldData, (draft) => {
          // insert the review back into the cache immutably
          draft.pages[pageIndex].reviews = [
            ...draft.pages[pageIndex].reviews.slice(0, reviewIndex),
            review,
            ...draft.pages[pageIndex].reviews.slice(reviewIndex),
          ];
        });
      });
    },
    onSuccess: (totalReviews, { nonProfitId }) => {
      // update the post cache
    },
  });
};

export const useReportCommunityReview = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['report-community-review'],
    mutationFn: async (review) => {
      const { nonProfitId, id: reviewId } = review;
      const res = await fetchViaProxy(
        `/proxy/v2/protected/nonprofits/${nonProfitId}/reviews/${reviewId}/report`,
        {
          method: 'POST',
        },
      );

      if (!res.ok) {
        throw new Error(`Error reporting review ${reviewId}`);
      }
    },
    onMutate: async (review) => {
      // update the state
      const { pageIndex, reviewIndex } = getReviewFromCache(
        review.nonProfitId,
        review.id,
        review.parentReviewId,
        queryClient,
      );
      removeReviewFromCache(review, queryClient);
      return { review, pageIndex, reviewIndex };
    },
    onError: (_err, _vars, context) => {
      const { review, pageIndex, reviewIndex } = context;
      const queryKey = getQueryKey(review.nonProfitId, review.parentReviewId);
      queryClient.setQueryData(queryKey, (oldData) => {
        return produce(oldData, (draft) => {
          // insert the review back into the cache immutably
          draft.pages[pageIndex].reviews = [
            ...draft.pages[pageIndex].reviews.slice(0, reviewIndex),
            review,
            ...draft.pages[pageIndex].reviews.slice(reviewIndex),
          ];
        });
      });
    },
  });
};

export const useUpdateReviewReaction = () => {
  const userProfile = useUserProfile();
  const selectedProfile = userProfile?.selectedProfile;
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: 'update-review-reaction',
    mutationFn: async ({ review, reaction }) => {
      const pathPart = reaction ? 'like' : 'unlike';
      const { nonProfitId, id: reviewId } = review;
      const path = `/proxy/v2/protected/nonprofits/${nonProfitId}/reviews/${reviewId}/${pathPart}`;

      const { data, ok } = await fetchViaProxy(path, {
        method: 'POST',
        body: JSON.stringify({
          as: selectedProfile,
          likeType: reaction,
        }),
      });

      if (!ok) {
        throw new Error(`Error reacting to review ${reviewId}`);
      }
      return data;
    },
    onMutate: ({ review, reaction }) => {
      const hasReaction = !isEmpty(reaction);
      const { nonProfitId, id: reviewId, parentReviewId } = review;
      const fromCache = getReviewFromCache(
        nonProfitId,
        reviewId,
        parentReviewId,
        queryClient,
      );
      const hadReaction = fromCache.review.userReaction.hasReaction;
      const cloned = cloneDeep(fromCache.review);
      cloned.userReaction = {
        hasReaction: hasReaction,
        reactionType: reaction,
        as: hasReaction ? selectedProfile : null,
      };

      if (!hadReaction && hasReaction) {
        cloned.totalReactions += 1;
      } else if (hadReaction && !hasReaction) {
        cloned.totalReactions -= 1;
      }

      // do an optimistic update
      updateReviewCache(cloned, queryClient, false);

      return {
        cached: fromCache.review,
      };
    },
    onSuccess: (serverData, { review }) => {
      const { nonProfitId, id: reviewId, parentReviewId } = review;
      // TODO: Shyam - handle reward
      const { userReaction, totalReactions, reactions, reward } = serverData;

      // apply the server response to the cache
      const queryKey = getQueryKey(nonProfitId, parentReviewId);
      queryClient.setQueryData(queryKey, (oldData) => {
        return produce(oldData, (draft) => {
          const { pageIndex, reviewIndex } = getReviewFromCache(
            nonProfitId,
            reviewId,
            parentReviewId,
            queryClient,
          );

          if (pageIndex >= 0 && reviewIndex >= 0) {
            const inCache = draft.pages[pageIndex].reviews[reviewIndex];
            inCache.userReaction = userReaction;
            inCache.totalReactions = totalReactions;
            inCache.reactions = reactions;
          } else {
            // this should never happen
            console.warn('Failed to update review cache');
          }
        });
      });
    },
    onError: (err, args, context) => {
      const { cached } = context;
      updateReviewCache(cached, queryClient, false);
    },
  });
};
